import { getDealRevenueCents, getDealPercentage, getDealInstallmentCents } from '../../../../shared/utils/dealRevenue';
import { entitySelectionPayload } from '../../../../shared/utils/entitySelection';
import { validateInstallmentAmounts } from './installments';

export const DEAL_KIND_META = {
  proposta: {
    title: 'Nuova Proposta',
    description: 'Inserisci tutti i dettagli per creare una nuova proposta commerciale',
    submitLabel: 'Crea Proposta',
    amountField: 'estimatedValue',
    amountLabel: 'Valore Stimato',
    scheduleDateField: 'dataPagamentoPrevista',
    scheduleDateLabel: 'Data Pagamento',
    startDateField: 'proposalDate',
    startDateLabel: 'Data Proposta',
    priorityField: 'priority',
    priorityLabel: 'Priorita',
  },
  inCorso: {
    title: 'Nuovo Affare',
    description: 'Inserisci tutti i dettagli per creare un nuovo affare',
    submitLabel: 'Crea Affare',
    amountField: 'value',
    amountLabel: 'Importo',
    scheduleDateField: 'dataPagamento',
    scheduleDateLabel: 'Data Pagamento',
    startDateField: 'dataInizio',
    startDateLabel: 'Data Inizio',
    priorityField: 'urgency',
    priorityLabel: 'Urgenza',
  },
  completato: {
    title: 'Affare Completato',
    description: 'Visualizza e aggiorna i dettagli dell\'affare completato',
    submitLabel: 'Salva Affare',
    amountField: 'value',
    amountLabel: 'Importo',
    scheduleDateField: 'completedDate',
    scheduleDateLabel: 'Data Completamento',
    startDateField: 'dataInizio',
    startDateLabel: 'Data Inizio',
    priorityField: 'urgency',
    priorityLabel: 'Urgenza',
  },
};

export const DEAL_TYPE_OPTIONS = [
  'Cessione Crediti Fiscali',
  'Cessione Crediti IVA',
  'Cessione Crediti R&D',
  'Cessione Crediti Energia',
  'Cessione Crediti Superbonus',
  'Cessione Crediti Commerciali',
  'Cessione Crediti Edilizi',
  'Cessione Crediti Formazione',
  'Cessione Crediti Transizione 4.0',
  'Cessione Crediti Investimenti',
  'Altro',
];

export const DEAL_STATUS_OPTIONS = [
  { value: 'proposta', label: 'Proposta', badgeClass: 'border-[var(--border-strong)] text-[var(--primary)] bg-[var(--primary-soft)]' },
  { value: 'inCorso', label: 'In Corso', badgeClass: 'border-[var(--warning-border)] text-[var(--warning-text)] bg-[var(--warning-soft)]' },
  { value: 'completato', label: 'Completato', badgeClass: 'border-[var(--success-border)] text-[var(--success-text)] bg-[var(--success-soft)]' },
];

export const DEAL_PRIORITY_OPTIONS = ['alta', 'media', 'bassa'];

export {
  commitInstallmentEdit,
  formatCentsForInput,
  isValidMoneyDraft,
  parseEuroToCents,
  rebalanceInstallments,
  splitInstallments,
  validateInstallmentAmounts,
} from './installments';

export function getDealKindMeta(dealType = 'inCorso') {
  return DEAL_KIND_META[dealType] || DEAL_KIND_META.inCorso;
}

export function normalizeDealForForm(deal = {}, dealType = 'inCorso') {
  const meta = getDealKindMeta(dealType);
  const normalizedScadenze =
    deal.dilazionamento?.scadenze && deal.dilazionamento.scadenze.length > 0
      ? [...deal.dilazionamento.scadenze]
      : ['', ''];

  return {
    id: deal.id,
    ...entitySelectionPayload(deal),
    clientId: deal.clientId ?? null,
    name: deal.name || '',
    client: deal.client || '',
    clientContact: deal.clientContact || '',
    clientEmail: deal.clientEmail || '',
    clientPhone: deal.clientPhone || '',
    description: deal.description || '',
    tipoAffare: deal.tipoAffare || '',
    value: deal.value || '',
    estimatedValue: deal.estimatedValue || '',
    probability: deal.probability ?? 100,
    stage: deal.stage || '',
    assignee: deal.assignee || '',
    staffId: deal.staffId ?? deal.assigneeStaffId ?? deal.assignedStaffId ?? null,
    teamCoinvolto: [...(deal.teamCoinvolto || [])],
    notes: deal.notes || '',
    origineAffare: deal.origineAffare || '',
    dataInizio: deal.dataInizio || deal.proposalDate || '',
    proposalDate: deal.proposalDate || deal.dataInizio || '',
    dataPagamento: deal.dataPagamento || '',
    dataPagamentoPrevista: deal.dataPagamentoPrevista || deal.dataPagamento || '',
    completedDate: deal.completedDate || deal.dataPagamento || '',
    priority: deal.priority || 'media',
    urgency: deal.urgency || 'media',
    paymentStatus: deal.paymentStatus || '',
    [meta.amountField]: deal[meta.amountField] || deal.value || deal.estimatedValue || '',
    [meta.scheduleDateField]:
      deal[meta.scheduleDateField] || deal.dataPagamento || deal.dataPagamentoPrevista || '',
    dilazionamento: {
      rate: deal.dilazionamento?.rate || normalizedScadenze.length,
      importoRata: deal.dilazionamento?.importoRata || '',
      importiRate: getDealInstallmentCents(deal, deal.dilazionamento?.rate || normalizedScadenze.length),
      scadenze: normalizedScadenze,
    },
    commenti: [...(deal.commenti || [])],
    customFields: { ...(deal.customFields || {}) },
  };
}

export function getInitialDealFormState(dealType = 'inCorso', stages = []) {
  return {
    ...normalizeDealForForm({}, dealType),
    stage: stages[0] || '',
    dataInizio: new Date().toISOString().split('T')[0],
    proposalDate: new Date().toISOString().split('T')[0],
  };
}

export function buildDealPayload(formState, dealType = 'inCorso') {
  const normalized = {
    ...formState,
    teamCoinvolto: (formState.teamCoinvolto || []).filter((member) => member.trim() !== ''),
    dilazionamento: {
      ...formState.dilazionamento,
      scadenze: (formState.dilazionamento?.scadenze || []).filter(Boolean),
    },
  };

  if (dealType === 'proposta') {
    normalized.proposalDate = normalized.dataInizio || normalized.proposalDate;
  }

  if (formState.customFields && Object.keys(formState.customFields).length > 0) {
    normalized.customFields = { ...formState.customFields };
  }

  return normalized;
}

export function validateDealForm(formState) {
  if (!formState.name || !formState.clientId || !formState.assignee) {
    return 'Compila i campi obbligatori: Nome, Cliente, Responsabile';
  }

  const percentage = getDealPercentage(formState);
  if (!Number.isFinite(percentage) || percentage < 0 || percentage > 100 || Math.abs(percentage * 100 - Math.round(percentage * 100)) > 0.000001) {
    return 'Inserisci una percentuale tra 0 e 100 con massimo due decimali';
  }
  if (formState.dilazionamento?.amountError) return formState.dilazionamento.amountError;
  if (Array.isArray(formState.dilazionamento?.importiRate)) {
    const totalCents = getDealRevenueCents(formState);
    const installmentError = validateInstallmentAmounts(formState.dilazionamento.importiRate, totalCents);
    if (installmentError) return installmentError;
  }

  return null;
}

export function getClientSummary(client) {
  if (!client) return null;

  return {
    company: client.company || client.businessName || '',
    contactName: client.name || '',
    email: client.contattiAziendali?.email || client.email || '',
    phone: client.contattiAziendali?.phone || client.phone || '',
  };
}

export function applyClientSummaryToDealForm(formState, client) {
  const summary = getClientSummary(client);
  if (!summary) return formState;

  return {
    ...formState,
    clientId: client.id ?? null,
    client: summary.company,
    clientContact: summary.contactName,
    clientEmail: summary.email,
    clientPhone: summary.phone,
  };
}

export function clearClientSummaryFromDealForm(formState) {
  return {
    ...formState,
    clientId: null,
    client: '',
    clientContact: '',
    clientEmail: '',
    clientPhone: '',
  };
}

export function formatDisplayDate(dateStr) {
  if (!dateStr) return 'N/A';

  return new Date(dateStr).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export function formatDisplayDateTime(dateStr) {
  if (!dateStr) return 'N/A';

  return new Date(dateStr).toLocaleString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Rome',
  });
}

export function formatDateForInput(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().split('T')[0];
}
